import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { studentsService, type Student } from "@/lib/services";
import { byRollNo } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table2, Loader2, Search, Trash2, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

type Col = { key: keyof Student; label: string; w: string; readOnly?: boolean };

const COLUMNS: Col[] = [
  { key: "gr_no", label: "GR No.", w: "w-24" },
  { key: "roll_no", label: "Roll No.", w: "w-20" },
  { key: "name", label: "Name", w: "w-56" },
  { key: "class_name", label: "Class", w: "w-24", readOnly: true },
  { key: "section", label: "Section", w: "w-20", readOnly: true },
  { key: "gender", label: "Gender", w: "w-24" },
  { key: "date_of_birth", label: "DOB", w: "w-28" },
  { key: "phone", label: "Phone 1", w: "w-32" },
  { key: "phone_2", label: "Phone 2", w: "w-32" },
  { key: "father_name", label: "Father Name", w: "w-48" },
  { key: "mother_name", label: "Mother Name", w: "w-48" },
  { key: "address_line_1", label: "Address 1", w: "w-48" },
  { key: "address_line_2", label: "Address 2", w: "w-48" },
  { key: "address_line_3", label: "Address 3", w: "w-48" },
  { key: "state", label: "State", w: "w-32" },
  { key: "pin_code", label: "Pin", w: "w-24" },
  { key: "social_category", label: "Social Category", w: "w-36" },
  { key: "aadhaar_no", label: "Aadhaar", w: "w-40" },
];

const classSort = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

export function MasterDataPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["students", "master"], queryFn: () => studentsService.list() });
  const [rows, setRows] = useState<Student[]>([]);
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState<Student | null>(null);

  useEffect(() => { if (data) setRows(data); }, [data]);

  const classes = useMemo(
    () => Array.from(new Set(rows.map((r) => r.class_name).filter(Boolean))).sort(classSort),
    [rows],
  );
  const sections = useMemo(() => {
    const pool = classFilter ? rows.filter((r) => r.class_name === classFilter) : rows;
    return Array.from(new Set(pool.map((r) => r.section).filter(Boolean) as string[])).sort(classSort);
  }, [rows, classFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => (!classFilter || r.class_name === classFilter))
      .filter((r) => (!sectionFilter || (r.section || "") === sectionFilter))
      .filter((r) => !q || `${r.name} ${r.gr_no || ""} ${r.roll_no || ""} ${r.father_name || ""}`.toLowerCase().includes(q))
      .sort((a, b) => classSort(a.class_name, b.class_name) || byRollNo(a as any, b as any));
  }, [rows, classFilter, sectionFilter, search]);

  const commitCell = async (student: Student, field: keyof Student, value: string) => {
    const patch = { [field]: value === "" ? null : value } as Partial<Student>;
    await studentsService.update(student.id, patch);
    setRows((prev) => prev.map((r) => (r.id === student.id ? { ...r, [field]: value } : r)));
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await studentsService.remove(deleting.id);
      setRows((prev) => prev.filter((r) => r.id !== deleting.id));
      qc.invalidateQueries({ queryKey: ["students"] });
      toast.success(`Deleted ${deleting.name}`);
    } catch (e: any) {
      toast.error(e.message || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="master-data-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Table2 className="h-6 w-6 text-primary" /> Student Master Data
        </h1>
        <p className="text-sm text-muted-foreground">Admin-only spreadsheet. Click any cell to edit — changes save instantly. Class &amp; Section are read-only here.</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <CardTitle className="text-base">Records</CardTitle>
              <CardDescription data-testid="master-data-count">{filtered.length} of {rows.length} student(s)</CardDescription>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs">Class</Label>
                <select
                  data-testid="master-class-filter"
                  className="mt-1 h-9 w-36 rounded-md border border-input bg-background px-2 text-sm"
                  value={classFilter}
                  onChange={(e) => { setClassFilter(e.target.value); setSectionFilter(""); }}
                >
                  <option value="">All classes</option>
                  {classes.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Section</Label>
                <select
                  data-testid="master-section-filter"
                  className="mt-1 h-9 w-28 rounded-md border border-input bg-background px-2 text-sm"
                  value={sectionFilter}
                  onChange={(e) => setSectionFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {sections.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Search</Label>
                <div className="relative mt-1">
                  <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="master-search"
                    className="h-9 w-56 pl-8"
                    placeholder="Name, GR No., Roll…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <div className="max-h-[70vh] overflow-auto rounded-md border">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-muted">
                  <tr className="text-left">
                    {COLUMNS.map((c) => (
                      <th key={String(c.key)} className={`whitespace-nowrap border-b border-r p-2 font-semibold ${c.w}`}>
                        {c.label}{c.readOnly && <span className="ml-1 text-[10px] font-normal text-muted-foreground">(locked)</span>}
                      </th>
                    ))}
                    <th className="sticky right-0 border-b bg-muted p-2 text-center font-semibold">Del</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr key={s.id} className="hover:bg-muted/30" data-testid={`master-row-${s.gr_no || s.id}`}>
                      {COLUMNS.map((c) => (
                        <td key={String(c.key)} className={`border-b border-r p-0 ${c.w}`}>
                          {c.readOnly ? (
                            <div className="px-2 py-1.5 text-muted-foreground">{(s[c.key] as any) ?? "—"}</div>
                          ) : (
                            <EditableCell
                              testId={`cell-${c.key}-${s.gr_no || s.id}`}
                              value={(s[c.key] as any) ?? ""}
                              onCommit={(v) => commitCell(s, c.key, v)}
                            />
                          )}
                        </td>
                      ))}
                      <td className="sticky right-0 border-b bg-white p-1 text-center">
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                          data-testid={`master-delete-${s.gr_no || s.id}`}
                          onClick={() => setDeleting(s)}
                          title="Delete student"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={COLUMNS.length + 1} className="p-8 text-center text-muted-foreground">No students match the filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {deleting && (
        <Dialog open onOpenChange={(o) => { if (!o) setDeleting(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" /> Delete student?
              </DialogTitle>
              <DialogDescription>
                This permanently removes <b>{deleting.name}</b> (GR No. {deleting.gr_no || "—"}, {deleting.class_name}) from the database. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleting(null)} data-testid="master-delete-cancel">Cancel</Button>
              <Button variant="destructive" onClick={confirmDelete} data-testid="master-delete-confirm">Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function EditableCell({ value, onCommit, testId }: { value: string; onCommit: (v: string) => Promise<void>; testId: string }) {
  const [val, setVal] = useState(value);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const initial = useRef(value);
  useEffect(() => { setVal(value); initial.current = value; }, [value]);

  const commit = async () => {
    if (val === initial.current) return;
    setStatus("saving");
    try {
      await onCommit(val);
      initial.current = val;
      setStatus("saved");
      setTimeout(() => setStatus("idle"), 1200);
    } catch (e: any) {
      setStatus("error");
      setVal(initial.current);
      toast.error(e.message || "Save failed");
    }
  };

  return (
    <div className="relative">
      <input
        data-testid={testId}
        className={`h-full w-full bg-transparent px-2 py-1.5 outline-none focus:bg-primary/5 focus:ring-1 focus:ring-primary ${status === "saved" ? "bg-emerald-50" : ""}`}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
      />
      {status === "saving" && <Loader2 className="absolute right-1 top-2 h-3.5 w-3.5 animate-spin text-primary" />}
      {status === "saved" && <Check className="absolute right-1 top-2 h-3.5 w-3.5 text-emerald-600" />}
    </div>
  );
}
